/**
 * API Service for Family and Subscription Management
 * All sensitive operations are handled by the backend
 */

import { BACKEND_CONFIG } from '../config/backendConfig';

// ── Global 401 handler (BUG_LIST #165) ───────────────────────────────────────
// Previously nothing caught 401s: on session expiry the user stayed "logged in"
// on pages whose every fetch failed with a raw "HTTP 401". When a request comes
// back 401 and the user actually had a session, clear it and bounce to /login,
// preserving where they were via `postLoginRedirect` so login can return them.
let unauthorizedRedirectInFlight = false;

/**
 * Exported so every service layer shares ONE session-expiry behaviour. The
 * coach portal (KGH-795) has its own client but must not have its own answer to
 * a 401 — two answers is how one of them ends up silently doing nothing.
 */
export function handleUnauthorized(endpoint: string): void {
  if (typeof window === 'undefined') return;
  // Let AuthContext own the mount-time validation paths — a 401 there already
  // falls back to Auth0 silent re-auth; hijacking it would force a needless
  // manual login for users whose Auth0 session is still alive.
  if (endpoint.startsWith('/auth/session') || endpoint.startsWith('/user/profile')) return;

  const hadSession =
    !!sessionStorage.getItem('sessionToken') || !!localStorage.getItem('sessionToken');
  // No stored session → this is a pre-login / public flow; let the caller handle it.
  if (!hadSession) return;

  const path = window.location.pathname;
  // Don't loop on auth/public surfaces.
  const skip = ['/login', '/callback', '/signup', '/invitation', '/verify', '/reset', '/p/', '/account-deleted'];
  if (skip.some((p) => path.startsWith(p))) return;
  if (unauthorizedRedirectInFlight) return;
  unauthorizedRedirectInFlight = true;

  try {
    const target = window.location.pathname + window.location.search;
    if (target && target !== '/' && !target.startsWith('/login')) {
      sessionStorage.setItem('postLoginRedirect', target);
    }
  } catch {
    /* sessionStorage may be unavailable */
  }
  sessionStorage.removeItem('sessionToken');
  localStorage.removeItem('sessionToken');
  // A coach whose session lapsed signs back in on the coach page.
  window.location.assign(path.startsWith('/coach') ? '/login?coach=1' : '/login');
}

// Types based on backend API requirements
export interface User {
  id: string;
  email: string;
  name?: string;
  user_type?: 'guardian' | 'athlete' | 'staff' | 'coach';
  family_id?: string;
  athlete_profile_id?: string;
  is_billing_owner: boolean;
  onboarding_step: number;
  onboarding_complete: boolean;
  deleted_at?: string | null; // ISO timestamp or null
  profile_picture_url?: string | null;
  phone?: string | null;
  organization_id?: string;
  org_name?: string;
  staff_role?: string;
  staff_title?: string;
  assigned_team_ids?: string[];
  family?: {
    id: string;
    name: string;
    member_count: number;
    has_subscription: boolean;
    has_athlete_profile: boolean;
  };
}

export interface Family {
  id: string;
  name: string;
  member_count: number;
  max_members: number;
  subscription_status?: string;
  members?: FamilyMember[];
  athlete_profiles?: AthleteProfile[];
}

export interface FamilyMember {
  id: string;
  email: string;
  user_type: string;
  is_billing_owner: boolean;
  joined_at: string;
  athlete_profile_id?: string;
}

export interface AthleteProfile {
  id: string;
  sport: string;
  grade: number;
  position?: string;
  created_by: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  metadata?: any;
  prices: {
    id: string;
    amount: number; // Amount in cents
    currency: string;
    interval: 'month' | 'year';
    interval_count: number;
    trial_period_days?: number;
  }[];
  features?: string[]; // Made optional since it might not be in API response
}

export interface Subscription {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  plan: {
    price_id: string;  // Added price_id as backend now provides this
    name: string;
    amount: number;    // Changed from 'price' to 'amount' to match backend
    currency: string;
    interval: string;
  };
  // Added discount information for coupon support
  discount?: {
    coupon: {
      id: string;
      name: string;
      percent_off?: number;
      amount_off?: number;
    };
    discount_amount: number;
    final_amount: number;
  };
  // Added scheduled changes for downgrades
  scheduled_change?: {
    effective_date: number;
    new_plan: {
      name: string;
      amount: number;
      currency: string;
      interval: string;
    };
  };
}

export interface Invitation {
  id: string;
  family: {
    name: string;
    member_count: number;
  };
  invited_by: {
    email: string;
  };
  anticipated_user_type: string;
  expires_at: string;
  is_valid: boolean;
}

// API utility function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const url = `${BACKEND_CONFIG.baseURL}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

    
  if (!response.ok) {
    // Session expired / revoked mid-use — clear it and redirect to login.
    if (response.status === 401) {
      handleUnauthorized(endpoint);
    }

    let error: any;
    try {
      error = await response.json();
    } catch (parseError) {
      error = {};
    }

    const errorMessage = error.error?.message || error.message || error.error || `HTTP ${response.status}: Unknown error`;
    // Attach the structured error body so callers can branch on .code
    // (e.g. invite-accept needs to distinguish INVITATION_EMAIL_MISMATCH
    // from a generic failure for the hard-block UX).
    const thrown: any = new Error(errorMessage);
    thrown.status = response.status;
    thrown.code = error.error?.code || error.code;
    thrown.details = error.error || error;
    throw thrown;
  }

  const responseData = await response.json();
    return responseData;
}

// Auth & User Management
export const authAPI = {
  async handleAuthCallback(auth0Token: string, inviteToken?: string): Promise<{
    user: User;
    api_token: string;
    next_action: string;
  }> {
    return apiCall('/auth/callback', {
      method: 'POST',
      body: JSON.stringify({
        auth0_token: auth0Token,
        invite_token: inviteToken,
      }),
    });
  },

  async signup(email: string, password: string, name: string): Promise<{
    user: User;
    token: string;
    message: string;
  }> {
    return apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  },

  async validateSession(token: string): Promise<{
    valid: boolean;
    user?: User;
  }> {
    return apiCall('/auth/session', { method: 'GET' }, token);
  },
};

// Coach engagement (Phase 1 monetization). Tiered by paid status server-side:
// free returns counts only; paid adds coach identities.
export interface CoachEngagement {
  coach_name: string | null;
  school_name: string | null;
  position: string | null;
  title: string | null;
  last_opened_at: string | null;
  open_count: number;
  engaged_this_week: boolean;
}
export interface CoachEngagementSummary {
  isPaid: boolean;
  engaged: boolean;
  count: number;
  this_week: number;
  coaches?: CoachEngagement[];
}
export const engagementAPI = {
  getCoaches(token: string, athleteId?: string): Promise<CoachEngagementSummary> {
    const qs = athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : '';
    return apiCall(`/engagement/coaches${qs}`, { method: 'GET' }, token);
  },
};

// Coach unlocks (Phase 1 §1.5). Free athletes unlock up to 3 coaches to reveal
// contact; paid are unlimited. unlock() uses a direct fetch so the 402 paywall
// response is returned (not thrown) for the caller to branch on.
export interface CoachUnlockResult {
  status: number; // 200 unlocked, 402 free_limit_reached
  unlocked?: boolean;
  isPaid?: boolean;
  paywall?: boolean;
  remaining?: number | null;
  coach?: { production_id?: string | number; coach_name?: string; email?: string; phone?: string; twitter_handle?: string; instagram_handle?: string; title?: string; position?: string } | null;
  // A PROGRAM unlock returns the whole staff (each with production_id) so the
  // client can reveal every coach at the school at once.
  coaches?: Array<{ production_id?: string | number; coach_name?: string; email?: string; phone?: string; twitter_handle?: string; instagram_handle?: string; title?: string; position?: string }>;
  error?: string;
}
export const coachUnlocksAPI = {
  getStatus(token: string, athleteId?: string): Promise<{ isPaid: boolean; unlimited?: boolean; revealedCount?: number; limit?: number; remaining?: number; schoolIds?: string[] }> {
    const qs = athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : '';
    return apiCall(`/coach-unlocks/status${qs}`, { method: 'GET' }, token);
  },
  async unlock(token: string, coachId: string, schoolId?: string, athleteId?: string): Promise<CoachUnlockResult> {
    const res = await fetch(`${BACKEND_CONFIG.baseURL}/coach-unlocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ coach_id: coachId, school_id: schoolId, athlete_id: athleteId }),
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, ...data };
  },
  // Unlock a whole PROGRAM (school) — reveals its entire staff, spends one of the
  // 3 free programs. Used by the bulk flow (each new school = one program).
  async unlockSchool(token: string, schoolId: string | number, athleteId?: string): Promise<CoachUnlockResult> {
    const res = await fetch(`${BACKEND_CONFIG.baseURL}/coach-unlocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ school_id: String(schoolId), athlete_id: athleteId }),
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, ...data };
  },
};

// APS-260. A family reports a coach change. It goes to staff review, so
// nothing changes on the live coach until someone checks it.
export type CoachReportKind = 'departed' | 'contact' | 'title' | 'missing';
export type CoachReportPosition = 'Head Coach' | 'Assistant Coach';
export interface CoachReportRequest {
  kind: CoachReportKind;
  school_id: number;
  /** Required for every kind except missing. */
  production_id?: number;
  /** The active athlete. A guardian always passes it. */
  athlete_id?: string;
  note?: string;
  email?: string;
  phone?: string;
  position?: CoachReportPosition;
  title?: string;
  coach_name?: string;
}
export type CoachReportOutcome = 'filed' | 'added' | 'already_reported' | 'no_change' | 'already_listed';
export interface CoachReportResult {
  outcome: CoachReportOutcome;
  message: string;
  reports_left_today: number;
}

export const coachReportsAPI = {
  /**
   * Errors throw with .status and .code from the server
   * (DAILY_LIMIT on 429, INVALID_EMAIL, INVALID_PHONE and so on).
   */
  send(token: string, report: CoachReportRequest): Promise<CoachReportResult> {
    return apiCall('/coach-reports', { method: 'POST', body: JSON.stringify(report) }, token);
  },
};

// APS-259 step 4. The new-user game plan for one athlete. The server decides
// every step from real data; the portal only renders it.
export type GamePlanStepKey = 'schools' | 'profile' | 'video' | 'gmail' | 'contact_coach';
export type GamePlanProfileGap = 'name' | 'photo' | 'team' | 'height';
export interface GamePlanStep {
  key: GamePlanStepKey;
  done: boolean;
  done_at: string | null;
  detail: {
    // schools
    count?: number;
    target?: number;
    // profile
    missing?: GamePlanProfileGap[];
    more?: number;
    // video
    featured_url?: string | null;
    uploads?: number;
    youtube_connected?: boolean;
    // gmail
    status?: 'connected' | 'needs_reauth' | 'none';
    // contact_coach
    emails?: number;
    x_posts?: number;
    x_dms?: number;
    coaches?: number;
    suggested_school_id?: number | null;
  };
}
export interface GamePlan {
  enabled: true;
  viewer: { role: 'athlete' | 'guardian'; user_id: string } | null;
  athlete: { id: string; first_name: string | null; is_self: boolean };
  welcome: { show: boolean; video_url: string; poster_url: string };
  checklist: { show: boolean; hidden: boolean };
  done_count: number;
  total: number;
  steps: GamePlanStep[];
  coach_views: { count: number; latest_school_name: string | null; latest_at: string | null };
  free_programs: { is_paid: boolean; limit: number; used: number; remaining: number };
  upgrade: { show: boolean };
}
export type GamePlanResponse = GamePlan | { enabled: false };

export const gamePlanAPI = {
  get(token: string, athleteId?: string): Promise<GamePlanResponse> {
    const qs = athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : '';
    return apiCall(`/game-plan${qs}`, { method: 'GET' }, token);
  },
  // Both answer 204 with no body, so they use fetch directly (apiCall parses JSON).
  async welcomeSeen(token: string): Promise<void> {
    const res = await fetch(`${BACKEND_CONFIG.baseURL}/game-plan/welcome-seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: '{}',
    });
    if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
  },
  async hide(token: string, athleteId: string): Promise<void> {
    const res = await fetch(`${BACKEND_CONFIG.baseURL}/game-plan/hide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ athlete_id: athleteId }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw Object.assign(new Error(body?.error?.message || body?.message || `HTTP ${res.status}`), {
        status: res.status,
        code: body?.code || body?.error?.code,
      });
    }
  },
};

// §0.4 — integration (Gmail/X/YouTube) connection health.
export interface IntegrationHealth {
  provider: 'gmail' | 'x' | 'youtube';
  status: 'connected' | 'needs_reauth';
  connected: boolean;
  needs_reauth: boolean;
  needs_reauth_at: string | null;
  reauth_reason: string | null;
  expires_at: string | null;
  last_refreshed: string | null;
}

export const integrationHealthAPI = {
  getHealth(
    token: string,
    athleteId: string
  ): Promise<{ success: boolean; athleteId: string; integrations: IntegrationHealth[] }> {
    return apiCall(`/integrations/${encodeURIComponent(athleteId)}/health`, { method: 'GET' }, token);
  },
};

// Public school pages (Phase 3 §3.4). Unauthenticated, PII-safe.
export interface PublicSchool {
  school_id: number | string;
  name: string;
  division?: string;
  conference?: string;
  logo_url?: string;
  head_coach?: string;
  college_url?: string;
  coaching_staff?: Array<{ coach_name: string; position?: string; title?: string }>;
  roster_needs?: Array<{ position: string; grad_year: number; count: number }>;
  academics?: {
    city?: string;
    state?: string;
    avg_cost_of_attendance?: number;
    avg_tuition_in_state?: number;
    avg_tuition_out_of_state?: number;
    undergraduate_student_count?: number;
    admission_rate?: number;
    avg_sat_score?: number;
    avg_act_score?: number;
  } | null;
  top_majors?: string[];
}
export const publicSchoolsAPI = {
  async get(slug: string): Promise<{ school: PublicSchool }> {
    const res = await fetch(`${BACKEND_CONFIG.baseURL}/public/schools/${encodeURIComponent(slug)}`);
    if (!res.ok) throw new Error(res.status === 404 ? 'not_found' : 'failed');
    return res.json();
  },
};

/** URL-safe school slug embedding the id: "university-of-michigan-1234". */
export function schoolSlug(name: string, schoolId: string | number): string {
  const s = (name || 'school')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${s}-${schoolId}`;
}

// Family Management
export const familyAPI = {
  async createFamily(token: string, name: string): Promise<{ family: Family }> {
    return apiCall('/families', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }, token);
  },

  async getMyFamily(token: string): Promise<{ family: Family }> {
    return apiCall('/families/me', { method: 'GET' }, token);
  },

  // GET /families/me/my-athletes — the athletes linked to the CURRENT user.
  // Athlete users get their own profile; guardians get the athletes they're
  // tied to via guardian_athlete_relationships.
  //
  // Phase 2.5: response includes the fields the client-side profile
  // completeness audit needs (useProfileCompleteness) so JIT banners on
  // Dashboard / College Lists can render without a second fetch.
  async getMyAthletes(token: string): Promise<{
    athletes: Array<{
      id: string;
      family_id: string;
      first_name: string;
      last_name: string;
      sport: string | null;
      grade: number | null;
      graduation_year: number | null;
      position: string | null;
      positions: string[] | null;
      city: string | null;
      state: string | null;
      date_of_birth: string | null;
      phone: string | null;
      ncaa_id: string | null;
      profile_picture_url: string | null;
      height: string | null;
      weight: number | null;
      bats: string | null;
      throws: string | null;
      awards: string[] | null;
      current_gpa: number | null;
      sat_total: number | null;
      act_total: number | null;
      performance_metrics: {
        exit_velocity: number | null;
        overhand_velocity: number | null;
        home_to_first: number | null;
      };
      school_name: string | null;
      travel_club_name: string | null;
      /* APS-259 game plan. The highlight video link (YouTube). */
      featured_video_url?: string | null;
      created_at: string;
    }>;
    total: number;
  }> {
    return apiCall('/families/me/my-athletes', { method: 'GET' }, token);
  },

  async getDetailedFamily(token: string): Promise<{ family: any }> {
    return apiCall('/families/me/detailed', { method: 'GET' }, token);
  },

  async updateFamily(token: string, updates: { name?: string }): Promise<{ family: Family }> {
    return apiCall('/families/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, token);
  },

  async inviteMember(
    token: string,
    email: string,
    anticipatedUserType?: 'guardian' | 'athlete' | null,
  ): Promise<{ invitation: { id: string; email: string; expires_at: string; invite_link: string } }> {
    return apiCall('/families/me/members/invite', {
      method: 'POST',
      body: JSON.stringify({
        email,
        anticipated_user_type: anticipatedUserType || undefined,
      }),
    }, token);
  },

  // Resend a pending invitation — backend burns the old row, issues a new
  // token + fresh 7-day expiry, and re-fires the email.
  async resendInvitation(
    token: string,
    invitationId: string,
  ): Promise<{ invitation: { id: string; email: string; expires_at: string } }> {
    return apiCall(`/families/me/invitations/${invitationId}/resend`, {
      method: 'POST',
    }, token);
  },

  // Cancel a pending invitation. Soft-burns the row; subsequent clicks of
  // the original email link 404.
  async cancelInvitation(token: string, invitationId: string): Promise<{ success: boolean }> {
    return apiCall(`/families/me/invitations/${invitationId}`, {
      method: 'DELETE',
    }, token);
  },

  // Remove a family member (billing-owner only). Drops the family_members
  // row + nulls users.family_id; the target user account is preserved.
  async removeMember(token: string, userId: string): Promise<{ success: boolean }> {
    return apiCall(`/families/me/members/${userId}`, {
      method: 'DELETE',
    }, token);
  },

  async validateInvitation(inviteToken: string): Promise<{
    valid: boolean;
    invitation: {
      // The target email of the invite — used to hard-block at accept
      // when the logged-in user's email doesn't match.
      email: string;
      family: { id: string; name: string; member_count: number; max_members: number };
      invited_by: { name: string; email: string };
      anticipated_user_type: 'guardian' | 'athlete' | null;
      expires_at: string;
      message?: string;
    };
  }> {
    return apiCall(`/families/invitations/${inviteToken}`, { method: 'GET' });
  },

  // Pre-flight inspection — returns which page the invite-link click
  // should land on (signup / login / auto_accept / family_conflict /
  // softwarn) without mutating state. Optional sessionToken: when
  // present, the destination reflects the signed-in user's situation.
  async preflightInvitation(inviteToken: string, sessionToken?: string | null): Promise<{
    ok: boolean;
    destination?:
      | 'signup'
      | 'login'
      | 'login_for_existing_member'
      | 'login_then_family_conflict'
      | 'auto_accept'
      | 'invitation_welcome_softwarn'
      | 'family_conflict'
      | 'already_member_redirect';
    invitation?: any;
    context?: any;
    error?: { code: string };
  }> {
    return apiCall(
      `/families/invitations/${inviteToken}/preflight`,
      { method: 'GET' },
      sessionToken || undefined,
    );
  },

  // Public — returns the family's athlete roster for a fresh invite
  // token. Used by the invited-combined onboarding screen to render the
  // athlete picker before the auto-accept transaction completes.
  async getInvitationFamilyAthletes(inviteToken: string): Promise<{
    profiles: Array<{
      id: string;
      first_name: string;
      last_name: string;
      sport: string;
      graduation_year: number | null;
    }>;
    total: number;
  }> {
    return apiCall(`/families/invitations/${inviteToken}/family-athletes`, { method: 'GET' });
  },

  async acceptInvitation(
    inviteToken: string,
    userData: {
      name: string;
      email: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      selected_user_type?: 'guardian' | 'athlete' | null;
    },
    token: string,
  ): Promise<{
    success: boolean;
    user_type?: string | null;
    email_mismatch?: boolean;
    family: { id: string; name: string; member_count: number };
    user: { id: string; name: string; email: string; family_id: string };
    session: { token: string; expires_at: string };
    message: string;
  }> {
    return apiCall(`/families/invitations/${inviteToken}/accept`, {
      method: 'POST',
      body: JSON.stringify({
        user: userData,
        first_name: userData.first_name,
        last_name: userData.last_name,
        phone: userData.phone,
        selected_user_type: userData.selected_user_type ?? null,
      }),
    }, token);
  },

  async getPendingInvitations(token: string): Promise<any> {
        const result = await apiCall('/families/me/invitations', { method: 'GET' }, token);
        return result;
  },

  async updateMemberProfileIds(
    token: string,
    userId: string,
    profileIds: {
      athlete_profile_id?: string;
      guardian_profile_id?: string;
    }
  ): Promise<{
    success: boolean;
    user: {
      id: string;
      athlete_profile_id: string | null;
      guardian_profile_id: string | null;
      updated_at: string;
    };
  }> {
    return apiCall(`/families/me/members/${userId}/profile-ids`, {
      method: 'PUT',
      body: JSON.stringify(profileIds),
    }, token);
  },
};

// NOTE: Stripe has been removed - all payments go through RevenueCat
// stripeAPI removed on 2026-01-08

// Athlete Profiles
export const athleteAPI = {
  async getMyProfile(token: string): Promise<{ 
    profile?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      graduationYear: number;
      city: string;
      state: string;
      sport?: string;
      grade?: number;
      position?: string;
      ncaaId?: string;
      createdBy: string;
      createdAt: string;
    }
  }> {
    return apiCall('/profiles/athlete-profiles/me', { method: 'GET' }, token);
  },

  async createProfile(
    token: string,
    profileData: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      dateOfBirth: string; // ISO date string (YYYY-MM-DD)
      graduationYear: number;
      city: string;
      state: string;
      sport?: string;
      grade?: number;
      position?: string;
      ncaaId?: string;
      stats?: object;
      achievements?: object;
    }
  ): Promise<{ 
    profile: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      graduationYear: number;
      ncaaId?: string;
      city: string;
      state: string;
      sport: string;
      grade: number;
      position: string;
      createdBy: string;
      createdAt: string;
    }
  }> {
    return apiCall('/profiles/athlete-profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    }, token);
  },

  async getFamilyProfiles(token: string, familyId: string): Promise<{ 
    profiles: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      sport: string;
      graduationYear: number;
      ncaaId?: string;
      city: string;
      state: string;
      createdBy: string;
      createdAt: string;
    }>;
    total: number;
  }> {
    return apiCall(`/profiles/athlete-profiles/family/${familyId}`, { method: 'GET' }, token);
  },

  async updateProfile(
    token: string,
    profileId: string,
    updates: Partial<{
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      graduationYear: number;
      city: string;
      state: string;
      sport: string;
      grade: number;
      position: string;
      ncaaId: string;
      youtubeChannelUrl: string;
      youtubeChannelHandle: string;
      /* APS-255. '' clears the column; omitting the key leaves it untouched. */
      xHandle: string;
      instagramHandle: string;
      /* APS-259. A YouTube link. '' clears it; omitting the key leaves it untouched. */
      featuredVideoUrl: string;
    }>
  ): Promise<{ 
    profile: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      graduationYear: number;
      ncaaId?: string;
      xHandle?: string | null;
      instagramHandle?: string | null;
      featuredVideoUrl?: string | null;
      city: string;
      state: string;
      sport: string;
      grade: number;
      position: string;
    }
  }> {
    return apiCall(`/profiles/athlete-profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, token);
  },

  async deleteProfile(token: string, profileId: string): Promise<{ message: string }> {
    return apiCall(`/profiles/athlete-profiles/${profileId}`, {
      method: 'DELETE',
    }, token);
  },

  // Sport Info Methods
  async getSoftballPositions(token: string): Promise<{ 
    positions: string[] 
  }> {
    return apiCall('/athlete/positions/softball', { method: 'GET' }, token);
  },

  async getSportInfo(token: string, athleteId: string): Promise<{ 
    sportInfo?: {
      sport: string;
      positions: string[];
      bats: string;
      throws: string;
      height: string;
      weight: number;
      awards: string[];
    } | null
  }> {
    return apiCall(`/athlete/sport-info?athleteId=${athleteId}`, { method: 'GET' }, token);
  },

  async updateSportInfo(
    token: string,
    athleteId: string,
    sportInfo: {
      sport: string;
      positions: string[];
      bats: string;
      throws: string;
      height: string;
      weight: number | null;
      awards: string[];
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiCall('/athlete/sport-info', {
      method: 'POST',
      body: JSON.stringify({
        athleteId,
        ...sportInfo,
      }),
    }, token);
  },

  // Dedicated endpoint for the Awards & Honors modal. Updates the athletic
  // awards (athlete_sport_info.awards) and academic achievements
  // (athlete_academic_achievements) in a single transaction.
  async updateAwardsAndHonors(
    token: string,
    athleteId: string,
    payload: { athletic_awards: string[]; academic_achievements: string[] }
  ): Promise<{
    success: boolean;
    message: string;
    athletic_awards: string[];
    academic_achievements: string[];
  }> {
    return apiCall('/athlete/awards-and-honors', {
      method: 'POST',
      body: JSON.stringify({
        athleteId,
        athletic_awards: payload.athletic_awards,
        academic_achievements: payload.academic_achievements,
      }),
    }, token);
  },

  // Performance Metrics Methods
  
  // Get available pitch types for pitcher metrics dropdown
  async getPitchTypes(token: string): Promise<{ 
    success: boolean;
    pitchTypes: string[];
  }> {
    return apiCall('/athletes/pitch-types', { method: 'GET' }, token);
  },

  async getPerformanceMetrics(token: string, athleteId: string): Promise<{ 
    success: boolean;
    data?: {
      id: string;
      athlete_id: string;
      metrics: {
        sixty_yard_time: number | null;
        forty_yard_time: number | null;
        twenty_yard_time: number | null;
        ten_yard_time: number | null;
        home_to_first: number | null;
        pop_time: number | null;
        overhand_velocity: number | null;
        exit_velocity: number | null;
        grip_strength: number | null;
        pitch_velocities: Array<{ pitch_type: string; value: number }>;
        spin_rates: Array<{ pitch_type: string; value: number }>;
      };
      sport: string;
      recorded_date: string;
      created_by: string;
      last_updated: string;
    } | null;
  }> {
    return apiCall(`/athletes/${athleteId}/performance-metrics`, { method: 'GET' }, token);
  },

  async createOrUpdatePerformanceMetrics(
    token: string,
    athleteId: string,
    metrics: {
      sixty_yard_time?: string;
      forty_yard_time?: string;
      twenty_yard_time?: string;
      ten_yard_time?: string;
      home_to_first?: string;
      pop_time?: string;
      overhand_velocity?: string;
      exit_velocity?: string;
      grip_strength?: string;
      recorded_date?: string;
      measurement_context?: string;
      notes?: string;
      pitch_velocities?: Array<{ pitch_type: string; value: number }>;
      spin_rates?: Array<{ pitch_type: string; value: number }>;
    }
  ): Promise<{ 
    success: boolean;
    data: {
      id: string;
      athlete_id: string;
      metrics: {
        sixty_yard_time: number | null;
        forty_yard_time: number | null;
        twenty_yard_time: number | null;
        ten_yard_time: number | null;
        home_to_first: number | null;
        pop_time: number | null;
        overhand_velocity: number | null;
        exit_velocity: number | null;
        grip_strength: number | null;
        pitch_velocities: Array<{ pitch_type: string; value: number }>;
        spin_rates: Array<{ pitch_type: string; value: number }>;
      };
      sport: string;
      recorded_date: string;
      created_by: string;
      created_at: string;
      updated_at: string;
    }
  }> {
    return apiCall(`/athletes/${athleteId}/performance-metrics`, {
      method: 'POST',
      body: JSON.stringify(metrics),
    }, token);
  },

  async getPerformanceHistory(
    token: string,
    athleteId: string,
    options?: {
      limit?: number;
      offset?: number;
      from_date?: string;
      to_date?: string;
    }
  ): Promise<{ 
    success: boolean;
    data: {
      current?: {
        id: string;
        athlete_id: string;
        metrics: {
          sixty_yard_time: number | null;
          forty_yard_time: number | null;
          twenty_yard_time: number | null;
          ten_yard_time: number | null;
          home_to_first: number | null;
          pop_time: number | null;
          overhand_velocity: number | null;
          exit_velocity: number | null;
          grip_strength: number | null;
        };
        sport: string;
        recorded_date: string;
        created_by: string;
        last_updated: string;
      };
      history: Array<{
        id: string;
        metrics: {
          sixty_yard_time: number | null;
          forty_yard_time: number | null;
          twenty_yard_time: number | null;
          ten_yard_time: number | null;
          home_to_first: number | null;
          pop_time: number | null;
          overhand_velocity: number | null;
          exit_velocity: number | null;
          grip_strength: number | null;
        };
        recorded_date: string;
        measurement_context: string | null;
        notes: string | null;
        recorded_by: string;
      }>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        has_more: boolean;
      };
    }
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.from_date) params.append('from_date', options.from_date);
    if (options?.to_date) params.append('to_date', options.to_date);
    
    const queryString = params.toString();
    const endpoint = `/athletes/${athleteId}/performance-metrics/history${queryString ? `?${queryString}` : ''}`;
    
    return apiCall(endpoint, { method: 'GET' }, token);
  },

  async deletePerformanceMetrics(
    token: string,
    athleteId: string
  ): Promise<{ 
    success: boolean;
    message: string;
  }> {
    return apiCall(`/athletes/${athleteId}/performance-metrics`, {
      method: 'DELETE',
    }, token);
  },

  // Academic Performance Methods
  async getAcademicPerformance(
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      academicPerformance: {
        satMath: string;
        satReadingWriting: string;
        satTotal: string;
        psatMath: string;
        psatReadingWriting: string;
        psatTotal: string;
        actTotal: string;
        gpaScale: string;
        currentGpa: string;
        achievements: string[];
      };
      lastUpdated: string | null;
    };
  }> {
    return apiCall(`/athletes/${athleteId}/onboarding/academic-performance`, {
      method: 'GET',
    }, token);
  },

  async updateAcademicPerformance(
    token: string,
    athleteId: string,
    data: {
      satMath?: string;
      satReadingWriting?: string;
      satTotal?: string;
      psatMath?: string;
      psatReadingWriting?: string;
      psatTotal?: string;
      actTotal?: string;
      gpaScale?: string;
      currentGpa?: string;
      achievements?: string[];
    }
  ): Promise<{
    success: boolean;
    data: {
      academicPerformance: any;
      message: string;
    };
  }> {
    return apiCall(`/athletes/${athleteId}/onboarding/academic-performance`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  },

  // Profile Picture Methods
  async uploadProfilePicture(
    token: string,
    athleteId: string,
    imageUri: string,
    fileName?: string,
    fileType?: string
  ): Promise<{
    success: boolean;
    data: {
      profilePictureUrl: string;
      thumbnails: {
        large: string;
        medium: string;
        small: string;
      };
      filename: string;
      fileSize: number;
      mimeType: string;
      uploadedAt: string;
    };
    message: string;
  }> {
    // Normalize file type for iOS compatibility
    let normalizedFileType = fileType || 'image/jpeg';
    let normalizedFileName = fileName || 'profile-picture.jpg';
    
    // Handle iOS-specific file type issues and normalize to backend-accepted types
    // Backend accepts: 'image/jpeg,image/png,image/webp'
    if (normalizedFileType === 'image/jpg') {
      normalizedFileType = 'image/jpeg';
    } else if (normalizedFileType === 'image/heic' || normalizedFileType === 'image/heif') {
      // iOS HEIC photos should be converted to JPEG
      normalizedFileType = 'image/jpeg';
      normalizedFileName = 'profile-picture.jpg';
    } else if (normalizedFileType.includes('jpeg') && normalizedFileType !== 'image/jpeg') {
      // Handle any JPEG variants
      normalizedFileType = 'image/jpeg';
    } else if (normalizedFileType.includes('png') && normalizedFileType !== 'image/png') {
      // Handle any PNG variants
      normalizedFileType = 'image/png';
    } else if (!['image/jpeg', 'image/png', 'image/webp'].includes(normalizedFileType)) {
      // If it's not one of the accepted types, default to JPEG
      normalizedFileType = 'image/jpeg';
      normalizedFileName = 'profile-picture.jpg';
    }
    
    // Ensure file extension matches MIME type
    if (normalizedFileType === 'image/jpeg' && !normalizedFileName.toLowerCase().includes('.jp')) {
      normalizedFileName = 'profile-picture.jpg';
    } else if (normalizedFileType === 'image/png' && !normalizedFileName.toLowerCase().includes('.png')) {
      normalizedFileName = 'profile-picture.png';
    }

    const formData = new FormData();
    formData.append('profilePicture', {
      uri: imageUri,
      type: normalizedFileType,
      name: normalizedFileName,
    } as any);

    const url = `${BACKEND_CONFIG.baseURL}/athletes/${athleteId}/profile-picture`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}: ${error.error || 'Upload failed'}`);
    }

    const responseData = await response.json();
    return responseData;
  },

  async getProfilePicture(
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      profilePictureUrl: string | null;
      thumbnails: {
        large: string;
        medium: string;
        small: string;
      } | null;
      filename: string | null;
      fileSize: number | null;
      mimeType: string | null;
      uploadedAt: string | null;
      lastUpdated: string | null;
    };
  }> {
    return apiCall(`/athletes/${athleteId}/profile-picture`, { method: 'GET' }, token);
  },

  async getProfilePictureSignedUrl(
    token: string,
    athleteId: string,
    expiresIn?: number
  ): Promise<{
    success: boolean;
    data: {
      signedUrl: string;
      expiresIn: number;
    };
  }> {
    const params = expiresIn ? `?expiresIn=${expiresIn}` : '';
    return apiCall(`/athletes/${athleteId}/profile-picture/signed-url${params}`, { method: 'GET' }, token);
  },

  async deleteProfilePicture(
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiCall(`/athletes/${athleteId}/profile-picture`, { method: 'DELETE' }, token);
  },

  // School Info Methods
  async getSchoolInfo(
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      schoolName: string;
      schoolJerseyNumber: string;
      schoolCoaches: Array<{
        name: string;
        email: string;
        phone: string;
        position: 'head_coach' | 'assistant_coach';
      }>;
      travelClubName: string;
      travelClubJerseyNumber: string;
      travelClubCoaches: Array<{
        name: string;
        email: string;
        phone: string;
        position: 'head_coach' | 'assistant_coach';
      }>;
    };
  }> {
    return apiCall(`/athletes/${athleteId}/onboarding/school-info`, { method: 'GET' }, token);
  },

  async updateSchoolInfo(
    token: string,
    athleteId: string,
    schoolInfo: {
      schoolName?: string;
      schoolJerseyNumber?: string;
      headCoachName?: string;
      headCoachEmail?: string;
      headCoachPhone?: string;
      assistantCoaches?: Array<{
        name: string;
        email: string;
        phone: string;
      }>;
      travelClubName?: string;
      travelClubJerseyNumber?: string;
      travelClubHeadCoachName?: string;
      travelClubHeadCoachEmail?: string;
      travelClubHeadCoachPhone?: string;
      travelClubAssistantCoaches?: Array<{
        name: string;
        email: string;
        phone: string;
      }>;
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiCall(`/athletes/${athleteId}/onboarding/school-info`, {
      method: 'PUT',
      body: JSON.stringify(schoolInfo),
    }, token);
  },

  // Onboarding Progress
  async updateOnboardingProgress(
    token: string,
    athleteId: string,
    progress: {
      completedStep: number;
      stepData?: {
        stepName: string;
        isComplete: boolean;
        completedAt: string;
      };
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiCall(`/athletes/${athleteId}/onboarding/progress`, {
      method: 'PUT',
      body: JSON.stringify(progress),
    }, token);
  },
};

// Guardian Profiles
export const guardianAPI = {
  async createProfile(
    token: string,
    profileData: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      relationshipToAthlete: string; // Father, Mother, Guardian, etc.
    }
  ): Promise<{ 
    profile: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      relationshipToAthlete: string;
      createdBy: string;
      createdAt: string;
    }
  }> {
    return apiCall('/profiles/guardian-profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    }, token);
  },

  async getFamilyProfiles(token: string, familyId: string): Promise<{ 
    profiles: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      relationshipToAthlete: string;
      athletes: Array<{
        athlete_id: string;
        athlete_name: string;
        relationship_type: string;
        verified: boolean;
      }>;
    }>;
    total: number;
  }> {
    return apiCall(`/profiles/guardian-profiles/family/${familyId}`, { method: 'GET' }, token);
  },

  async createBulkProfiles(
    token: string,
    profileData: {
      guardian: {
        firstName: string;
        lastName: string;
        email?: string;
        phone?: string;
        relationshipToAthlete: string;
      };
      athlete: {
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        dateOfBirth: string;
        graduationYear: number;
        city: string;
        state: string;
        sport?: string;
        grade?: number;
        position?: string;
      };
      secondGuardian?: {
        firstName: string;
        lastName: string;
        email?: string;
        phone?: string;
        relationshipToAthlete: string;
      };
    }
  ): Promise<{
    guardian?: any;
    athlete?: any;
    secondGuardian?: any;
  }> {
    return apiCall('/profiles/guardian-profiles/bulk', {
      method: 'POST',
      body: JSON.stringify(profileData),
    }, token);
  },

  async updateProfile(
    token: string,
    profileId: string,
    updates: Partial<{
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      relationshipToAthlete: string;
    }>
  ): Promise<{ 
    profile: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      relationshipToAthlete: string;
      /** Your new login name when it changed, else null. */
      userName: string | null;
    }
  }> {
    return apiCall(`/profiles/guardian-profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, token);
  },

  async deleteProfile(token: string, profileId: string): Promise<{ message: string }> {
    return apiCall(`/profiles/guardian-profiles/${profileId}`, {
      method: 'DELETE',
    }, token);
  },
};

// Family Profile Status (for smart onboarding)
export const profileStatusAPI = {
  async getFamilyProfileStatus(token: string, familyId: string): Promise<{
    hasAthleteProfile: boolean;
    athleteProfileCount: number;
    guardianProfileCount: number;
    profiles: {
      athletes: Array<{
        id: string;
        firstName: string;
        lastName: string;
        sport: string;
        createdByName: string;
      }>;
      guardians: Array<{
        id: string;
        firstName: string;
        lastName: string;
        relationshipToAthlete: string;
      }>;
    };
  }> {
    return apiCall(`/profiles/families/${familyId}/profile-status`, { method: 'GET' }, token);
  },
};

// User API
export const userAPI = {
  async getProfile(token: string): Promise<User> {
    return apiCall('/user/profile', { method: 'GET' }, token);
  },

  async getOnboardingStep(token: string): Promise<{ onboarding_step: number; onboarding_complete: boolean }> {
    return apiCall('/user/onboarding', { method: 'GET' }, token);
  },

  async updateOnboardingStep(
    token: string,
    step: number
  ): Promise<{ onboarding_step: number; onboarding_complete: boolean }> {
    // onboarding_step_completed / onboarding_complete are now fired server-side
    // from PUT /user/onboarding so they cover every client path (this wrapper,
    // direct fetch, mobile). See server.js handler.
    return apiCall<{ onboarding_step: number; onboarding_complete: boolean }>(
      '/user/onboarding',
      { method: 'PUT', body: JSON.stringify({ step }) },
      token
    );
  },

  async updateUserType(
    token: string,
    userType: 'athlete' | 'guardian'
  ): Promise<{ user_type: string; message: string }> {
    return apiCall('/user/user-type', {
      method: 'PUT',
      body: JSON.stringify({ user_type: userType }),
    }, token);
  },

  /**
   * Freemium-redesign Phase 1: POST /onboarding/quickstart.
   * Single transactional endpoint that creates the family (if needed),
   * writes the athlete or guardian profile, links via guardian_athlete_relationships
   * if applicable, and sets onboarding_step=100.
   *
   * Returns the full updated user shape matching GET /user/profile, so the
   * caller can push it into AuthContext via setUser() before navigating to
   * /dashboard. The route guards (isAdminAlsoUser etc.) then see fresh state.
   */
  async quickStart(
    token: string,
    payload: {
      user_type: 'athlete' | 'non_athlete_ui';
      sport?: string;
      class_year?: number;
      // Required when creating an athlete (athlete branch OR non-athlete with
      // no athlete_profile_id). Backend enforces against the canonical
      // softball position list and 2-letter US state codes.
      position?: string;
      state?: string;
      invite_token?: string;
      athlete_profile_id?: string;
      // Used by the non_athlete_ui no-invite-or-empty-family branch:
      // server creates the athlete in this family and links via
      // guardian_athlete_relationships.
      athlete_first_name?: string;
      athlete_last_name?: string;
      // APS-259. The signed-in person's own name, sent only when users.name is
      // empty or an email address (Apple sign-in).
      user_first_name?: string;
      user_last_name?: string;
      // APS-259. Optional YouTube link for the new athlete.
      featured_video_url?: string;
    }
  ): Promise<User & { already_onboarded?: boolean }> {
    return apiCall('/onboarding/quickstart', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, token);
  },

  async getComprehensiveProfile(token: string): Promise<{
    family: {
      id: string;
      name: string;
      maxMembers: number;
      athleteProfileCount: number;
      guardianProfileCount: number;
      hasAthleteProfile: boolean;
      createdAt: string;
      userJoinedAt: string;
    };
    athletes: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      graduationYear: number;
      ncaaId?: string;
      /* APS-255 — the family/profile read the athlete's own profile page uses. */
      xHandle?: string | null;
      instagramHandle?: string | null;
      city: string;
      state: string;
      sport: string;
      grade: string;
      position: string;
      stats: any;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
      sportInfo: {
        sport: string;
        positions: string;
        bats: string;
        throws: string;
        height: number;
        weight: number;
        awards: any;
        createdAt: string;
        updatedAt: string;
      };
      academicPerformance: {
        currentGpa: number;
        gpaScale: number;
        gpaAsOfDate: string;
        satScores: {
          total: number;
          math: number;
          readingWriting: number;
          testDate: string;
        };
        actScores: {
          total: number;
          testDate: string;
        };
        psatScores: {
          total: number;
          math: number;
          readingWriting: number;
          testDate: string;
        };
        createdAt: string;
        updatedAt: string;
      };
      performanceMetrics: {
        speed: {
          homeToFirst: number;
          sixtyYardTime: number;
          fortyYardTime: number;
          twentyYardTime: number;
          tenYardTime: number;
        };
        catching: {
          popTime: number;
        };
        strength: {
          gripStrength: number;
          exitVelocity: number;
          overhandVelocity: number;
        };
        sport: string;
        recordedDate: string;
        measurementContext: string;
        notes: string;
        createdAt: string;
        updatedAt: string;
      };
      guardians: Array<{
        id: string;
        name: string;
        email: string;
        phone: string;
        relationshipType: string;
        verified: boolean;
        createdAt: string;
      }>;
    }>;
    guardians: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      relationshipToAthlete: string;
      createdBy: string;
      userId: string;
      createdAt: string;
      updatedAt: string;
      athletes: Array<{
        id: string;
        name: string;
        sport: string;
        position: string;
        graduationYear: number;
        relationshipType: string;
        verified: boolean;
      }>;
    }>;
  }> {
    return apiCall('/profiles/me', { method: 'GET' }, token);
  },
};

// Account Management API
export const accountAPI = {
  async deleteAccount(token: string): Promise<{
    success: boolean;
    deletedAt: string;
    hardDeleteDate: string;
    gracePeriodDays: number;
  }> {
    return apiCall('/account/delete', {
      method: 'POST',
    }, token);
  },

  async reactivateAccount(token: string): Promise<{
    success: boolean;
    reactivatedAt: string;
  }> {
    return apiCall('/account/reactivate', {
      method: 'POST',
    }, token);
  },

  async getDeletionStatus(token: string): Promise<{
    deleted: boolean;
    deletedAt: string | null;
    hardDeleteDate: string | null;
    daysRemaining: number | null;
    canReactivate: boolean;
  }> {
    return apiCall('/account/deletion-status', {
      method: 'GET',
    }, token);
  },
};

// Tournament API
export interface Tournament {
  id: string;
  athlete_profile_id: string;
  name: string;
  facility_name: string | null;
  address1: string | null;
  address2: string | null;
  city: string;
  state: string;
  zip: string | null;
  start_date: string; // ISO date string
  end_date: string; // ISO date string
  notes: string | null;
  created_at: string;
  updated_at: string;
  games?: TournamentGame[];
  /**
   * KGH-795 / APS-255. The canonical event this row was normalised onto, so that
   * every family who typed a different spelling of the same tournament is
   * looking at one event. null when the row could not be keyed (no name, no
   * two-letter state, no start date) — the schedule row is still perfectly
   * valid, it just does not join the shared list.
   */
  event_id?: string | null;
  /**
   * How many college programs have said they will be at that event AND chose to
   * publish it. Present on the list read; 0 when the row has no event.
   */
  coaches_attending?: number;
}

/** One program that has marked a tournament the athlete is going to. */
export interface TournamentCoachProgram {
  school_id: number;
  school_name: string;
  /** 'D-I' | 'D-II' | 'D-III' | 'NAIA' | 'JUCO' | … or null. Never guess a division. */
  division: string | null;
  conference: string | null;
  logo_url: string | null;
  /** The coaches from that program who marked it. Can be empty. */
  coach_names: string[];
  marked_at: string;
}

export interface TournamentCoachesResponse {
  success: boolean;
  data: {
    tournament_id: string;
    event_id: string | null;
    event_name: string;
    programs: TournamentCoachProgram[];
    /**
     * What the family controls. `schedule_shared` false means this athlete does
     * NOT appear on the coach's side of the same event — the coaches are still
     * listed, but nobody there can see the player.
     */
    visibility: { schedule_shared: boolean; coaches_allowed: boolean };
  };
}

export interface TournamentGame {
  id: string;
  tournament_id: string;
  game_date: string; // ISO date string
  game_time: string | null; // HH:MM:SS format
  opponent: string;
  field_number: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface TournamentFormData {
  athlete_profile_id: string;
  name: string;
  facility_name?: string;
  address1?: string;
  address2?: string;
  city: string;
  state: string;
  zip?: string;
  start_date: string;
  end_date: string;
  notes?: string;
}

export interface GameFormData {
  tournament_id: string;
  game_date: string;
  game_time?: string;
  opponent: string;
  field_number?: string;
  notes?: string;
}

export const tournamentAPI = {
  // Get all tournaments for an athlete
  getTournaments: async (
    token: string,
    athleteProfileId: string,
    filter: 'upcoming' | 'past' | 'all' = 'all'
  ): Promise<{ success: boolean; data: { tournaments: Tournament[] } }> => {
    return apiCall(
      `/tournaments/${athleteProfileId}?filter=${filter}`,
      { method: 'GET' },
      token
    );
  },

  /**
   * KGH-795 / APS-255 item 11: the college programs that have said they will be
   * at this tournament. Joined through the canonical event, so a program that
   * marked a different spelling of the same tournament still appears.
   */
  getTournamentCoaches: async (
    token: string,
    tournamentId: string
  ): Promise<TournamentCoachesResponse> => {
    return apiCall(`/tournaments/${tournamentId}/coaches`, { method: 'GET' }, token);
  },

  // Create new tournament
  createTournament: async (
    token: string,
    tournamentData: Partial<TournamentFormData>
  ): Promise<{ success: boolean; data: { tournament: Tournament } }> => {
    return apiCall(
      '/tournaments',
      {
        method: 'POST',
        body: JSON.stringify(tournamentData),
      },
      token
    );
  },

  // Update tournament
  updateTournament: async (
    token: string,
    tournamentId: string,
    tournamentData: Partial<TournamentFormData>
  ): Promise<{ success: boolean; data: { tournament: Tournament } }> => {
    return apiCall(
      `/tournaments/${tournamentId}`,
      {
        method: 'PUT',
        body: JSON.stringify(tournamentData),
      },
      token
    );
  },

  // Delete tournament
  deleteTournament: async (
    token: string,
    tournamentId: string
  ): Promise<{ success: boolean; message: string }> => {
    return apiCall(
      `/tournaments/${tournamentId}`,
      { method: 'DELETE' },
      token
    );
  },

  // Add game to tournament
  addGame: async (
    token: string,
    tournamentId: string,
    gameData: Partial<GameFormData>
  ): Promise<{ success: boolean; data: { game: TournamentGame } }> => {
    return apiCall(
      `/tournaments/${tournamentId}/games`,
      {
        method: 'POST',
        body: JSON.stringify(gameData),
      },
      token
    );
  },

  // Bulk add games
  bulkAddGames: async (
    token: string,
    tournamentId: string,
    games: Partial<GameFormData>[]
  ): Promise<{ success: boolean; data: { games: TournamentGame[] } }> => {
    return apiCall(
      `/tournaments/${tournamentId}/games/bulk`,
      {
        method: 'POST',
        body: JSON.stringify({ games }),
      },
      token
    );
  },

  // Update game
  updateGame: async (
    token: string,
    gameId: string,
    gameData: Partial<GameFormData>
  ): Promise<{ success: boolean; data: { game: TournamentGame } }> => {
    return apiCall(
      `/tournaments/games/${gameId}`,
      {
        method: 'PUT',
        body: JSON.stringify(gameData),
      },
      token
    );
  },

  // Delete game
  deleteGame: async (
    token: string,
    gameId: string
  ): Promise<{ success: boolean; message: string }> => {
    return apiCall(
      `/tournaments/games/${gameId}`,
      { method: 'DELETE' },
      token
    );
  },

  // Get public schedule (no auth required)
  getPublicSchedule: async (
    athleteProfileId: string
  ): Promise<{
    success: boolean;
    data: {
      athlete: {
        first_name: string;
        last_name: string;
        sport: string;
        graduation_year: number;
        profile_picture_url: string | null;
      };
      tournaments: Tournament[];
    };
  }> => {
    const response = await fetch(
      `${BACKEND_CONFIG.baseURL}/tournaments/schedule/public/${athleteProfileId}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch public schedule');
    }
    return response.json();
  },
};

// Athlete Sharing API - Public Profile Sharing & Privacy Settings
export interface PrivacySettings {
  share_enabled: boolean;
  share_personal_info: boolean;
  share_contact_info: boolean;
  share_athletic_info: boolean;
  share_performance_metrics: boolean;
  share_team_info: boolean;
  share_academic_info: boolean;
  share_awards: boolean;
  share_tournament_schedule: boolean;
  share_past_tournaments: boolean;
  share_social_links: boolean;
  /*
   * Migration 049. share_social_links is the family's old yes to a YouTube
   * channel they typed in themselves; share_social_handles is a second,
   * separate yes to publishing the X / Instagram HANDLE on the unauthenticated
   * share page. The public page requires both, so this key is never the whole
   * answer on its own.
   *
   * Optional because the settings row is returned by a SELECT *: a database
   * where 049 has not been applied (prod, until 042 + 049 promote together)
   * simply has no such column and the key is absent rather than false. The
   * modal reads that absence as "this feature is not here yet" instead of
   * rendering a switch whose save the API would silently ignore.
   *
   * social_handles_set_at is the API's timestamptz as JSON. It is written by
   * the backend, never sent by us; null means the family has never been asked,
   * which is not the same as their having said no.
   */
  share_social_handles?: boolean;
  social_handles_set_at?: string | null;
  profile_headline: string | null;
}

export const athleteSharingAPI = {
  // Get privacy settings for an athlete
  getShareSettings: async (
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      settings: PrivacySettings;
      isEnabled: boolean;
      shareUrl: string | null;
      viewCount?: number;
      lastViewedAt?: string | null;
      lastSharedAt?: string | null;
    };
  }> => {
    return apiCall(`/athlete/${athleteId}/share/settings`, { method: 'GET' }, token);
  },

  // Enable sharing for an athlete profile
  enableSharing: async (
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      settings: PrivacySettings;
      shareUrl: string;
    };
  }> => {
    return apiCall(
      `/athlete/${athleteId}/share/enable`,
      { method: 'POST' },
      token
    );
  },

  // Update privacy settings
  updateSettings: async (
    token: string,
    athleteId: string,
    settings: Partial<PrivacySettings>
  ): Promise<{
    success: boolean;
    data: {
      settings: PrivacySettings;
    };
  }> => {
    return apiCall(
      `/athlete/${athleteId}/share/settings`,
      {
        method: 'PUT',
        body: JSON.stringify(settings),
      },
      token
    );
  },

  // Regenerate custom slug
  regenerateSlug: async (
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      settings: PrivacySettings;
      shareUrl: string;
    };
  }> => {
    return apiCall(
      `/athlete/${athleteId}/share/regenerate-slug`,
      { method: 'POST' },
      token
    );
  },

  // Get public profile (no auth required)
  getPublicProfile: async (
    slug: string
  ): Promise<{
    success: boolean;
    data: any;
  }> => {
    return apiCall(`/athlete/public/${slug}`, { method: 'GET' });
  },
};

// Support API
export const supportAPI = {
  async contactSupport(
    token: string,
    data: {
      name: string;
      email: string;
      subject: string;
      message: string;
      athleteId?: string;
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiCall('/support/contact', {
      method: 'POST',
      // Backend expects user_info.email (matching the mobile client payload),
      // not a flat email field — otherwise it 400s with "User email is required".
      body: JSON.stringify({
        subject: data.subject,
        message: data.message,
        user_info: {
          name: data.name,
          email: data.email,
          ...(data.athleteId ? { athleteId: data.athleteId } : {}),
        },
        timestamp: new Date().toISOString(),
      }),
    }, token);
  },
};

// Email Templates API
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string | null;
  body: string;
  scope?: 'global' | 'org' | 'personal';
  org_id?: string | null;
  org_name?: string | null;
  is_editable?: boolean;
  is_system?: boolean;
  created_at: string;
  updated_at: string;
}

export const emailTemplatesAPI = {
  // Get all templates for current user
  getTemplates: async (
    token: string
  ): Promise<{
    success: boolean;
    templates: EmailTemplate[];
  }> => {
    return apiCall('/email-templates', { method: 'GET' }, token);
  },

  // Get single template by ID
  getTemplate: async (
    token: string,
    templateId: string
  ): Promise<{
    success: boolean;
    template: EmailTemplate;
  }> => {
    return apiCall(`/email-templates/${templateId}`, { method: 'GET' }, token);
  },

  // Create new template
  createTemplate: async (
    token: string,
    data: { name: string; subject?: string; body: string }
  ): Promise<{
    success: boolean;
    template: EmailTemplate;
  }> => {
    return apiCall('/email-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  },

  // Update template
  updateTemplate: async (
    token: string,
    templateId: string,
    data: { name: string; subject?: string; body: string }
  ): Promise<{
    success: boolean;
    template: EmailTemplate;
  }> => {
    return apiCall(`/email-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  },

  // Delete template
  deleteTemplate: async (
    token: string,
    templateId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    return apiCall(`/email-templates/${templateId}`, { method: 'DELETE' }, token);
  },
};

// X (Twitter) Tag Lists API — user-managed lists of @handles for tweet composing
// (KGH-785). Mirrors emailTemplatesAPI; handles come back normalized (no leading @).
export interface XTagList {
  id: string;
  name: string;
  handles: string[];
  created_at: string;
  updated_at: string;
}

export const xTagListsAPI = {
  // Get all tag lists for the current user
  getTagLists: async (
    token: string
  ): Promise<{ success: boolean; tagLists: XTagList[] }> => {
    return apiCall('/x-tag-lists', { method: 'GET' }, token);
  },

  // Create a tag list
  createTagList: async (
    token: string,
    data: { name: string; handles: string[] }
  ): Promise<{ success: boolean; tagList: XTagList }> => {
    return apiCall('/x-tag-lists', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  },

  // Update a tag list
  updateTagList: async (
    token: string,
    tagListId: string,
    data: { name: string; handles: string[] }
  ): Promise<{ success: boolean; tagList: XTagList }> => {
    return apiCall(`/x-tag-lists/${tagListId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  },

  // Delete a tag list
  deleteTagList: async (
    token: string,
    tagListId: string
  ): Promise<{ success: boolean; message: string }> => {
    return apiCall(`/x-tag-lists/${tagListId}`, { method: 'DELETE' }, token);
  },
};

/**
 * RevenueCat API - Subscription status and promo code redemption
 */
export const revenueCatAPI = {
  // Get current subscription status from RevenueCat.
  // tier/hasPremiumAccess can still report a legacy premium_access grant
  // (Premium retired 2026-09-11) — read it as active access, never display it.
  getSubscriptionStatus: async (
    token: string
  ): Promise<{
    isActive: boolean;
    tier: 'none' | 'regular' | 'premium';
    hasRegularAccess: boolean;
    hasPremiumAccess: boolean;
    productId: string | null;
    expirationDate: string | null;
    store: string | null; // 'app_store', 'play_store', 'stripe', 'rc_billing', or 'promotional'
    willRenew: boolean;
    managementUrl: string | null;
    familyId: string;
    familyName: string;
  }> => {
    return apiCall('/revenuecat/status', { method: 'GET' }, token);
  },

  // Validate a promo code without redeeming it
  validatePromoCode: async (
    token: string,
    code: string,
    selectedPlan: 'regular' // one plan (Premium retired 2026-09-11)
  ): Promise<{
    valid: boolean;
    type?: 'free' | 'discount';
    code?: string;
    description?: string;
    entitlement?: string;
    appliesTo?: string;
    applies_to?: string;
    durationDays?: number | null;
    duration_days?: number | null;
    isLifetime?: boolean;
    // Discount code fields
    discount_type?: 'percent_off' | 'amount_off';
    discount_value?: number;
    stripe_coupon_id?: string;
    // Team pre-fill from promo
    team?: { team_name: string; org_name: string; sport: string; org_team_id: string };
    error?: string;
    message?: string;
  }> => {
    return apiCall('/revenuecat/validate-promo', {
      method: 'POST',
      body: JSON.stringify({ code, selectedPlan }),
    }, token);
  },

  // Redeem a promo code and grant entitlement
  redeemPromoCode: async (
    token: string,
    code: string,
    selectedPlan: 'regular', // one plan (Premium retired 2026-09-11)
    cancelExistingSubscription?: boolean
  ): Promise<{
    success: boolean;
    message: string;
    entitlement?: string;
    plan?: string;
    expiresAt?: string | null;
    isLifetime?: boolean;
    error?: string;
    // Confirmation fields for existing paid subscription
    requiresConfirmation?: boolean;
    confirmationMessage?: string;
    currentSubscription?: {
      tier: string;
      store: string;
      productId: string;
      expirationDate: string;
      willRenew: boolean;
      managementUrl?: string;
    };
    // New: User must cancel via portal/store first
    requiresCancellationFirst?: boolean;
    managementUrl?: string;
  }> => {
    // Make the API call directly to handle 409 confirmation response
    const url = `${BACKEND_CONFIG.baseURL}/revenuecat/redeem-promo`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ code, selectedPlan, cancelExistingSubscription }),
    });

    const data = await response.json();

    // Handle 409 - user needs to cancel first (portal or app store)
    if (response.status === 409 && data.requiresCancellationFirst) {
      return {
        success: false,
        message: data.message,
        error: data.error,
        requiresCancellationFirst: true,
        managementUrl: data.managementUrl,
        currentSubscription: data.currentSubscription,
      };
    }

    // Handle 409 - requires confirmation to cancel paid subscription (Google Play)
    if (response.status === 409 && data.requiresConfirmation) {
      return {
        success: false,
        message: data.message,
        error: data.error,
        requiresConfirmation: true,
        confirmationMessage: data.confirmationMessage,
        currentSubscription: data.currentSubscription,
      };
    }

    // Handle other errors
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to redeem promo code',
        error: data.error,
      };
    }

    return data;
  },

  // Get promo details for the current user's family
  getPromoDetails: async (
    token: string
  ): Promise<{
    hasPromo: boolean;
    code?: string;
    description?: string;
    entitlement?: string;
    isLifetime?: boolean;
    durationDays?: number | null;
    expiresAt?: string | null;
    redeemedAt?: string;
    discountType?: 'free' | 'percent_off' | 'amount_off';
    discountValue?: number;
    /** KGH-790: true when the family's access comes from a team code (lifetime while the team is active). */
    teamCode?: boolean;
    message?: string;
    error?: string;
  }> => {
    return apiCall('/revenuecat/promo-details', { method: 'GET' }, token);
  },
};

// Stripe Checkout API
export const stripeAPI = {
  // Create a Stripe Checkout session for subscription purchase
  createCheckoutSession: async (
    token: string,
    plan: 'regular', // one plan (Premium retired 2026-09-11)
    promoCode?: string,
    returnUrl?: string, // Optional: where to return on cancel (e.g., '/subscription-required')
    triggerAction?: string // P2.3: paywall trigger that led to checkout; rides in stripe_session.metadata so the webhook can attribute paywall_converted
  ): Promise<{
    success: boolean;
    sessionId?: string;
    url?: string;
    error?: string;
  }> => {
    return apiCall('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ plan, promoCode, returnUrl, trigger_action: triggerAction }),
    }, token);
  },

  // Get checkout session status
  getSessionStatus: async (
    token: string,
    sessionId: string
  ): Promise<{
    success: boolean;
    status?: string;
    paymentStatus?: string;
    customerEmail?: string;
    subscriptionId?: string;
    error?: string;
  }> => {
    return apiCall(`/stripe/session-status/${sessionId}`, { method: 'GET' }, token);
  },
};

// Team Code API - For athletes to join teams via invite codes
export const teamCodeAPI = {
  async validateCode(token: string, code: string): Promise<{
    success: boolean;
    valid: boolean;
    team?: {
      team_id: string;
      team_name: string;
      age_group?: string;
      season?: string;
      org_name: string;
      sport?: string;
      spots_remaining?: number;
    };
    error?: string;
  }> {
    return apiCall('/team-code/validate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }, token);
  },

  async redeemCode(token: string, code: string, athleteProfileId?: string): Promise<{
    success: boolean;
    membership?: {
      id: string;
      team_name: string;
      org_name: string;
    };
    // KGH-790: the family's resulting access basis ('lifetime' | 'none' | ...).
    access?: string;
    // KGH-790 round 8: only when access is 'none' — why (e.g.
    // 'team_code_signup_closed') and a plain sentence to show the user.
    accessReason?: string;
    accessMessage?: string;
    // KGH-790 round 8: false while the RevenueCat sync is still finishing in
    // the background; the DB already holds the access, so no UI hinges on it.
    rcSynced?: boolean;
    error?: string;
  }> {
    const body: Record<string, string> = { code };
    if (athleteProfileId) body.athlete_profile_id = athleteProfileId;
    return apiCall('/team-code/redeem', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  },

  async getMyTeams(token: string, athleteProfileId?: string): Promise<{
    success: boolean;
    teams: Array<{
      id: string;
      team_id: string;
      team_name: string;
      org_name: string;
      sport?: string;
      joined_via_code?: string;
      team_code?: string;
    }>;
  }> {
    const url = athleteProfileId
      ? `/team-code/my-teams?athlete_profile_id=${encodeURIComponent(athleteProfileId)}`
      : '/team-code/my-teams';
    return apiCall(url, {
      method: 'GET',
    }, token);
  },
};

// Notifications REST API (audit #234)
// Socket is the primary channel; these are the fallback path used by
// NotificationContext when the live socket is not connected, so a mark-read /
// delete still persists server-side instead of being a fire-and-forget emit
// that never reaches the server. Endpoints mirror the socket actions 1:1.
export const notificationsAPI = {
  async markRead(token: string, notificationId: string): Promise<{ success: boolean }> {
    return apiCall(`/notifications/${notificationId}/read`, { method: 'PATCH' }, token);
  },

  async markAllRead(token: string): Promise<{ success: boolean }> {
    return apiCall('/notifications/read-all', { method: 'PATCH' }, token);
  },

  async remove(token: string, notificationId: string): Promise<{ success: boolean }> {
    return apiCall(`/notifications/${notificationId}`, { method: 'DELETE' }, token);
  },

  async removeAll(token: string): Promise<{ success: boolean }> {
    return apiCall('/notifications', { method: 'DELETE' }, token);
  },
};